import React, { useContext, useCallback, useState, useMemo, useEffect } from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { ImageSuggestionCarousel } from '@bingads-webui-campaign-react/image-suggestion-carousel';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Spinner } from '@fluentui/react-components';
import { Shimmer, ShimmerElementsGroup, ShimmerElementType, MessageBar, MessageBarType } from '@fluentui/react';
import { CroppedImageView } from '@bingads-webui-campaign-react/cropped-image-view';
import { useCopilotLoggers } from '@bingads-webui-campaign-react/copilot-loggers';
import { isCopilotRegionRestricted } from '@bingads-webui-react/copilot-utils';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { AllRecommendationsPanel } from './view-all-recommendations-panel';
import { getCarouselStyles, getFullyShownImagesCount, initialImageContainerLeftMargin } from './carousel-style';
import { getImageSuggestionCarouselStyles } from './styles';

const SuggestionViewImageCount = 5;
const MaxSuggestionsInCarousel = 15;

const LoadingShimmer = () => {
  const i18n = useContext(I18nContext);

  const shimmerElements = useMemo(() => {
    const shimmerElement = [];

    _.each(_.range(SuggestionViewImageCount), () => {
      shimmerElement.push({ type: ShimmerElementType.line, height: 76, width: 106 });
      shimmerElement.push({ type: ShimmerElementType.gap, width: 10, height: 76 });
    });

    return shimmerElement;
  }, []);

  return (
    <div style={{ display: 'flex', width: '410px', flexWrap: 'wrap' }}>
      <Spinner labelPosition="after" size="extra-tiny" label={i18n.getString(_TL_('Generating new asset recommendations...'))} />
      <Shimmer style={{ width: '100%' }} customElementsGroup={<ShimmerElementsGroup width="90px" shimmerElements={shimmerElements} />} />
    </div>
  );
};

const getImageDimension = src => new Promise((resolve) => {
  const img = new Image();
  img.src = src;
  img.onload = () => {
    const width = img.naturalWidth;
    const height = img.naturalHeight;

    resolve({
      imgWidth: width, imgHeight: height,
    });
  };
});

const ImageView = ({ src }) => {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  const aspectRatio = width / height;
  const maxWidth = 107;

  const resizeHeight = Math.min(maxWidth / aspectRatio, 76);

  useEffect(() => {
    getImageDimension(src).then(({ imgWidth, imgHeight }) => {
      setWidth(imgWidth);
      setHeight(imgHeight);
    });
  }, [src]);

  if (!width || !height) {
    return <div />;
  }

  return (
    <CroppedImageView
      src={src}
      originalWidth={width}
      aspectRatio={aspectRatio}
      resizeImageHeight={resizeHeight}
      SourceWidth={width}
    />
  );
};

ImageView.propTypes = {
  src: PropTypes.string.isRequired,
};

export const DisplayAdsImageSuggestionCarousel = ({
  maxUploadNum = 5,
  onSelectImageSuggestion,
  useBrandKit = false,
  isBrandKitEmpty,
  setUseBrandKit,
}) => {
  const [isSeeAllPanelOpen, setIsSeeAllPanelOpen] = useState(false);
  const [imagesToSuggest, setImagesToSuggest] = useState([]);
  const [isCopilotEnabled, setIsCopilotEnabled] = useState(false);
  const activity = useContext(ActivityContext);

  const [recommendationContext, , reportRecommendations] = useContext(RecommendationContext);
  const { logSuggestionsViewed } = useCopilotLoggers();
  const permissions = useContext(PermissionsContext);
  const serverContext = useContext(ServerContext);
  const isBrandKitEnabled = permissions && permissions.dynamic && permissions.dynamic.BrandKitUI;

  const {
    imagesToSuggest: imageSuggestions,
    isLoading,
    groupedAds,
    errorMessage,
  } = recommendationContext || {};

  const i18n = useContext(I18nContext);

  const classes = useDefaultStyles(getImageSuggestionCarouselStyles);

  const { carouselState } = useMemo(() => ({
    carouselState: {
      carousel: {
        isError: _.isEmpty(imageSuggestions),
      },
      suggestion: {
        images: imageSuggestions,
      },
    },
    panelState: {
      panelImages: imageSuggestions,
      hasMore: false,
    },
  }), [imageSuggestions]);


  const { suggestion, carousel } = carouselState || {};
  const { images: suggestionImages } = suggestion || {};

  const onOffsetChange = useCallback((offset) => {
    const viewedImages = imagesToSuggest.slice(offset, offset + SuggestionViewImageCount);
    const suggestionIds = _.pluck(viewedImages, 'suggestionId');
    reportRecommendations({ view: suggestionIds });
    logSuggestionsViewed(suggestionIds);
  }, [imagesToSuggest, reportRecommendations, logSuggestionsViewed]);

  const onSelectImage = useCallback((fullUrl, index) => {
    // get all the groups for this base image.
    const allGroupedAds = groupedAds[fullUrl];

    // get thumbnail url from index
    const { thumbnailUrl } = imagesToSuggest[index];

    // find which group this thumbnail url belongs to
    const ads = _.find(allGroupedAds, group => _.find(group, ad => ad.url === thumbnailUrl));

    onSelectImageSuggestion(ads);
  }, [groupedAds, imagesToSuggest, onSelectImageSuggestion]);

  const onSelectSeeAllImages = useCallback(() => {
    const suggestionIds = suggestionImages.map(({ suggestionId }) => suggestionId);

    setIsSeeAllPanelOpen(true);
    logSuggestionsViewed(suggestionIds);
  }, [logSuggestionsViewed, suggestionImages]);

  const onRenderImage = useCallback((src, classesProp) => (
    <div className={classNames(classesProp.image, classes.croppedImage)}>
      <ImageView src={src} />
    </div>
  ), [classes.croppedImage]);

  const onBrandKitToggle = useCallback((value) => {
    if (activity) {
      activity.signal(`[DisplayAdsCopilot] - useBrandKitToggle changed to ${value}`);
    }
    setUseBrandKit(value);
  }, [activity, setUseBrandKit]);

  useEffect(() => {
    const truncatedImages = _.isArray(suggestionImages) && suggestionImages.slice(0, MaxSuggestionsInCarousel);
    setImagesToSuggest(truncatedImages || []);
  }, [suggestionImages]);

  useEffect(() => {
    if (isBrandKitEnabled) {
      isCopilotRegionRestricted({
        feature: 'Recommendations',
        permissions,
        serverContext,
      }).then(isRestricted => setIsCopilotEnabled(!isRestricted));
    } else {
      setIsCopilotEnabled(false);
    }
  }, [isBrandKitEnabled, permissions, serverContext]);

  return (
    <div className={classes.imageSuggestionCarouselRoot}>
      { isLoading ?
        <LoadingShimmer /> : (
          <ImageSuggestionCarousel
            title={_TL_('Recommended creatives')}
            helpTopic="pop_BA_DisplayAds_CreativeRecs"
            isLoading={isLoading}
            images={maxUploadNum > 0 ? imagesToSuggest : []}
            i18n={i18n}
            isEmpty={carousel && carousel.isError}
            onSelectImage={onSelectImage}
            onSelectSeeAll={onSelectSeeAllImages}
            showEllipsisWhenNoMoreImages
            showViewAllInHeader
            enableBrandKit={isBrandKitEnabled && !isBrandKitEmpty && isCopilotEnabled}
            useBrandKit={useBrandKit}
            setUseBrandKit={onBrandKitToggle}
            stylingOverrides={{
            getCarouselStyles,
            getFullyShownImagesCount,
            initialImageContainerLeftMargin,
            seeAllLinkText: _TL_('View all'),
          }}
            onOffsetChange={onOffsetChange}
            onRenderImage={onRenderImage}
          />
          )}
      {isSeeAllPanelOpen && (
        <AllRecommendationsPanel
          isOpen={isSeeAllPanelOpen}
          onDismiss={() => setIsSeeAllPanelOpen(false)}
          onSelectImageSuggestion={onSelectImageSuggestion}
        />
      )}
      {
        errorMessage && (
          <MessageBar messageBarType={MessageBarType.error}>
            {i18n.getString(errorMessage)}
          </MessageBar>
        )
      }
    </div>
  );
};

DisplayAdsImageSuggestionCarousel.propTypes = {
  maxUploadNum: PropTypes.number.isRequired,
  onSelectImageSuggestion: PropTypes.func.isRequired,
  useBrandKit: PropTypes.bool,
  isBrandKitEmpty: PropTypes.bool,
  setUseBrandKit: PropTypes.func,
};
